import React, { useState, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import * as R from 'ramda';

import Main from '../styles/Main/Main';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import LaddaButton from 'App/components/LaddaButton';
import useFetchBranchesQuery from 'App/hooks/useFetchBranchesQuery';
import useGenerateActivityMobileLinksMutation from 'App/hooks/useGenerateActivityMobileLinksMutation';
import useActivityMobileLinksPresignedUrl from 'App/hooks/useActivityMobileLinksPresignedUrl';
import downloadFileByBlob from 'App/utils/downloadFileByBlob';
import { SelectInput } from 'Shared/form/SelectField';
import './PatientMessagingStyles.scss';

const propTypes = {};
const defaultProps = {};

function Link2Text() {
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [downloadId, setDownloadId] = useState(null);
    const { mutateAsync: generateLinks, isLoading: isGenerating } = useGenerateActivityMobileLinksMutation();
    const { mutateAsync: getPresignedUrl, isLoading: isGettingPresignedUrl } = useActivityMobileLinksPresignedUrl();
    const { isLoading: isFetchingBranches, data: branchesData } = useFetchBranchesQuery();

    const branches = useMemo(() => {
        return branchesData ? R.compose(R.sortBy(R.prop('name')), R.values)(branchesData) : [];
    }, [branchesData]);

    const handleGenerateLinks = async () => {
        if (selectedBranch?.id) {
            const response = await generateLinks(selectedBranch.id);
            const nextDownloadId = R.path(['downloadId'], response) || null;
            setDownloadId(nextDownloadId);
        }
    };

    const handleDownloadLink = async () => {
        if (selectedBranch?.id && downloadId) {
            try {
                const response = await getPresignedUrl({
                    branchId: selectedBranch.id,
                    downloadId,
                });
                const presignedUrl = R.path(['presignedUrl'], response);
                const csvBody = R.path(['csvBody'], response);

                if (typeof presignedUrl === 'string' && presignedUrl.length > 0) {
                    window.location.replace(presignedUrl);
                } else if (typeof csvBody === 'string' && csvBody.length > 0) {
                    // local environment returns the CSV content directly instead of a presigned URL
                    const contentType = R.path(['contentType'], response) || 'text/csv';
                    const fileName = R.path(['fileName'], response) || 'patient-activity-mobile-links.csv';
                    downloadFileByBlob(csvBody, fileName, contentType);
                }
            } catch (error) {
                // Toast/logging already handled by the axios interceptor; this only prevents an unhandled rejection.
                // eslint-disable-next-line no-console
                console.error(error);
                // downloadId may have expired server-side; force the user to regenerate links.
                setDownloadId(null);
            }
        }
    };

    return (
        <Main fluid className="link-2-text">
            <h1>Link2Text</h1>
            <Row>
                <Col md={12}>
                    <p>Generate mobile activity links to download and send to patients outside of TherigySTM.</p>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={2}>
                    <SelectInput
                        name="branch-select"
                        aria-label="Select Branch"
                        options={branches}
                        getOptionValue={R.prop('id')}
                        getOptionLabel={R.prop('name')}
                        isClearable
                        isLoading={isFetchingBranches}
                        onChange={(branch) => {
                            setSelectedBranch(branch);
                            setDownloadId(null);
                        }}
                        value={selectedBranch}
                        placeholder="Select Branch"
                    />
                </Col>
            </Row>

            <FormButtonGroup className="form-button-group">
                <LaddaButton
                    variant="primary"
                    isLoading={isGenerating}
                    disabled={isGenerating || !selectedBranch}
                    onClick={handleGenerateLinks}
                    aria-label="Generate Link(s)"
                >
                    Generate Link(s)
                </LaddaButton>
                <LaddaButton
                    variant="primary"
                    isLoading={isGettingPresignedUrl}
                    disabled={isGettingPresignedUrl || !selectedBranch || !downloadId}
                    onClick={handleDownloadLink}
                    aria-label="Download to CSV"
                >
                    Download to CSV
                </LaddaButton>
            </FormButtonGroup>
        </Main>
    );
}

Link2Text.propTypes = propTypes;
Link2Text.defaultProps = defaultProps;

export default Link2Text;
